<template>
  <div class="editor-view-container overflow-hidden h-100">
    <EditorSkeletonLoader
      v-if="pageLoading"
      :side-by-side-view="!!sideBySideView"
    />

    <EditorLayout
      class="h-100 w-100"
      :side-by-side-view="sideBySideView"
    >
      <template
        v-if="editorMode"
        #full-width-header
      >
        <v-sheet
          max-width="1800px"
          class="mx-auto"
        >
          <SideBySideTopBar v-if="sideBySideView" />
          <ChromeTopBar v-else-if="isChromeView" />
          <TopBar v-else />
        </v-sheet>
      </template>

      <template
        v-if="!pageLoading"
        #content
      >
        <AppAlertsProvider v-if="isChromeView" />
        <JobProfileEditorContent
          v-if="editorMode"
          class="w-100"
        />
        <JobProfileLimitedContent
          v-else
        />
      </template>
      <template
        v-if="editorMode"
        #left
      >
        <LeftSideBar />
      </template>
      <template
        v-if="editorMode"
        #right
      >
        <RightSideBar v-if="!pageLoading" />
      </template>

      <template #content-footer>
        <EditorToolbarContainer
          class="pt-1 pb-1"
          :show-comments="true"
        />
      </template>
    </EditorLayout>

    <JobProfileCompareModal />
  </div>
</template>

<script setup>
import {
  nextTick, onMounted, watch, computed,
} from 'vue';
import { useHead } from '@unhead/vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppRoute } from '@/stores/app/route';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useFirstDraft } from '@/stores/document/firstDraft';
import { useSideBySideView } from '@/stores/document/sideBySideView';
import { useDocumentRightPanel } from '@/stores/document/rightPanel';
import EditorSkeletonLoader from '@/components/shared/SkeletonLoaders/editor/EditorSkeletonLoader.vue';
import EditorLayout from '@/components/editor/EditorLayout.vue';
import JobProfileEditorContent from '@/components/jobProfiles/editor/JobProfileEditorContent.vue';
import JobProfileLimitedContent from '@/components/jobProfiles/editor/JobProfileLimitedContent.vue';
import JobProfileCompareModal from '@/components/jobProfiles/editor/JobProfileCompareModal.vue';
import LeftSideBar from '@/components/jobProfiles/editor/LeftSideBar.vue';
import RightSideBar from '@/components/jobProfiles/editor/RightSideBar.vue';
import TopBar from '@/components/jobProfiles/editor/TopBar.vue';
import SideBySideTopBar from '@/components/jobProfiles/editor/SideBySideTopBar.vue';
import EditorToolbarContainer from '@/components/document/editorToolbar/EditorToolbarContainer.vue';
import ChromeTopBar from '@/components/jobProfiles/editor/ChromeTopBar.vue';
import AppAlertsProvider from '@/components/shared/alerts/AppAlertsProvider.vue';
import { useTracking } from '@/stores/tracking/tracking';
import { getJpEditorProfileType } from '@/helpers/jobProfileLibraryTracking';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  isHris: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();

const { pageLoading } = storeToRefs(useAppLoading());

const { checkRbacPermissions } = useAppAuthData();

const { route, isChromeView } = storeToRefs(useAppRoute());

const { sideBySideView } = storeToRefs(useSideBySideView());

const {
  summaryFieldId, jobProfile, isTemplate, hasEditAccess,
} = storeToRefs(useCurrentJobProfile());

const editorMode = computed(() => (props.isHris
  ? checkRbacPermissions(['initHrisProfileEditor'])
  : (checkRbacPermissions(['editAnyDraftProfile']) || hasEditAccess.value)));

const pageTitle = computed(() => {
  const draftType = isTemplate.value ? 'Template' : '';
  const type = props.isHris ? 'HRIS Profile' : draftType;
  return `${jobProfile.value?.title || ''}${type ? ` - ${type}` : ''}`;
});

useHead({ title: () => pageTitle.value });

const load = async () => {
  const start = Date.now();
  const { loadJobProfile } = useCurrentJobProfile();

  useTracking().jpEditorPage({
    ulid: props.id,
    profileType: props.isHris ? 'job_profile_hris' : 'job_profile',
  });

  await loadJobProfile({
    ulid: props.id, isHris: props.isHris, rescore: true,
  });

  useTracking().jpEditorLoaded({
    ulid: props.id,
    profileType: getJpEditorProfileType({
      isHris: props.isHris,
      isTemplate: isTemplate.value,
    }),
    title: jobProfile.value?.title,
    duration: Date.now() - start,
  });
};

onMounted(() => {
  load();
});

watch(() => props.id, () => {
  load();
});

const VALID_SIDEBAR_TABS = ['history', 'flsa', 'approvals', 'comments', 'template-usage', 'access-list'];

watch(pageLoading, (loading) => {
  if (loading) return;

  const q = route.value?.query || {};
  const hasWriteWithFirstDraft = Boolean(q.writeWithFirstDraft);
  const sidebarTab = VALID_SIDEBAR_TABS.includes(q.sidebar) ? q.sidebar : null;

  if (!hasWriteWithFirstDraft && !sidebarTab) return;

  if (hasWriteWithFirstDraft) {
    useFirstDraft().generateAndApplyJobProfile({ ulid: props.id, summaryFieldId: summaryFieldId.value });
  }

  nextTick(() => {
    if (sidebarTab) {
      useDocumentRightPanel().setCurrentTab(sidebarTab, { track: false });
    }

    if (hasWriteWithFirstDraft) {
      const nextQuery = { ...q };

      delete nextQuery.writeWithFirstDraft;

      router.replace({ query: nextQuery }).catch(() => {});
    }
  });
});
</script>
